import { Box, Grid, Stack, Typography } from "@mui/material";
import LanguageContext from "components/LanguageContext";
import Wrapper from "components/LayoutFront/Wrapper";
import SiteContext from "components/SiteContext";
import Block from "components/templatesComponents/Block";
import Icon from "components/templatesComponents/Icon";
import Link from "components/templatesComponents/Link";
import Summary from "components/templatesComponents/Summary";
import TemplateBanner from "components/templatesComponents/TemplateBanner";
import { useFindPages } from "hooks/page.hook";
import PropTypes from "prop-types";
import React, { useContext, useMemo, useState } from "react";
import t from "utils/locales/translation.json";
import { normalized } from "utils/urlUtils";

const ProcessDsfr = (props) => {
  const { page: pageVersion = {} } = props;

  const { contents, page, pageId, shortDescription, title } = pageVersion;

  const {
    hasMobileDemarche,
    dynamicItem,
    alertInfo,
    browserDemarche,
    mobileDemarche,
    stepper,
    dynamicPart,
    storeLinks,
    browserDemarcheButton,
    displaySummary,
    displayAlternative,
  } = contents;

  const { originalPageId } = page || {};

  const [isBrowserDemarche, setIsBrowserDemarche] = useState(true);

  const bo = useMemo(() => typeof hasMobileDemarche === "object", [hasMobileDemarche]);

  const site = useContext(SiteContext);
  const { language, setLanguage } = useContext(LanguageContext);
  const { id: siteId } = site || {};

  const { data: associatedPnuPages = [] } = useFindPages({
    siteId,
    pageId: originalPageId || pageId,
  });

  const translatedPage = useMemo(() => {
    return associatedPnuPages?.find((p) => p.page?.lang !== language);
  }, [associatedPnuPages, language]);

  const stepList = useMemo(() => {
    let stepArr;
    const stepperChildren = bo ? stepper.props.children : stepper.props.steps;
    if (stepperChildren) {
      if (!Array.isArray(stepperChildren)) {
        stepArr = [stepperChildren];
      } else {
        stepArr = stepperChildren;
      }
      return stepArr?.map((step, index) => {
        const title = bo ? step.props.content.children[0].value : step.props.title;
        const link = `${index + 1}-${normalized(title)}`;
        return {
          title,
          link,
        };
      });
    }
    return [];
  }, [stepper, bo]);

  return (
    <Box sx={{ bgcolor: "var(--background-default-grey)" }}>
      <TemplateBanner bo={bo}>
        <Stack sx={{ "& *": { textAlign: "left" } }}>
          <Block sx={{ p: 0 }}>
            <Stack sx={{ flexGrow: 1 }}>
              <h1>{title}</h1>
              <Stack
                direction="row"
                sx={[
                  {
                    alignItems: "center",
                  },
                  bo
                    ? {
                        mb: 1,
                        "& label": { mr: 0 },
                        "& p": { mb: 0.25, opacity: displayAlternative?.props?.content?.value === "true" ? 1 : 0.3 },
                      }
                    : {},
                ]}
              >
                {bo ? displayAlternative : null}
                {displayAlternative && (
                  <p className="fr-text--sm">
                    {t[language].processDsfr.do_process_1}{" "}
                    <Link
                      className="fr-text--sm"
                      url={translatedPage?.fullPath}
                      onClick={() => (translatedPage ? setLanguage(translatedPage.page.lang) : null)}
                    >
                      {t[language].processDsfr.do_process_2}
                      <Icon iconDSFR="translate-2" size="xs" sx={{ ml: "0.2rem", mb: "0.5rem" }} />
                    </Link>
                  </p>
                )}
              </Stack>
              <Box component="p" sx={{ mb: { lg: 2 } }} className="fr-text--xl">
                {shortDescription}
              </Box>
              {dynamicItem && (
                <Stack
                  direction={{ lg: "column" }}
                  sx={{ gap: 2, mb: { lg: 3, xs: 1 }, "& a ": { width: { xs: "100%", lg: "auto" } } }}
                >
                  {dynamicItem}
                </Stack>
              )}
              <Stack
                direction={{ xs: "column", lg: "row" }}
                sx={{
                  justifyContent: "space-between",
                  gap: { xs: 4, lg: 1 },
                  ...(!bo ? { alignItems: "center" } : {}),
                  "& a": { width: { xs: "100%", lg: "fit-content" } },
                  "& button": {
                    width: { xs: "100%", lg: "fit-content" },
                  },
                }}
              >
                {isBrowserDemarche && browserDemarcheButton}

                {hasMobileDemarche && (
                  <Box sx={{ alignContent: "end", ...(!isBrowserDemarche ? { width: "100%" } : {}) }}>
                    {bo ? (
                      <Box
                        sx={{
                          ...(bo ? { opacity: hasMobileDemarche.props?.content?.value === "true" ? 1 : 0.3 } : {}),
                        }}
                      >
                        {storeLinks}

                        <button type="button" className="fr-btn fr-btn--tertiary fr-btn--sm">
                          {browserDemarche}
                        </button>
                        <button type="button" className="fr-btn fr-btn--tertiary fr-btn--sm">
                          {mobileDemarche}
                        </button>
                      </Box>
                    ) : (
                      <Box>
                        {isBrowserDemarche ? (
                          <button
                            type="button"
                            className="fr-btn fr-btn--tertiary fr-btn--sm"
                            onClick={() => setIsBrowserDemarche(!isBrowserDemarche)}
                          >
                            {mobileDemarche}
                          </button>
                        ) : (
                          <Stack
                            direction={{ xs: "column", lg: "row" }}
                            sx={{
                              justifyContent: "space-between",
                              alignItems: "center",
                              "& button": {
                                height: "100%",
                              },
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", lg: "row" }}
                              sx={{ "& a": { width: "inherit" }, mb: { xs: 2, lg: 0 } }}
                            >
                              {storeLinks}
                            </Stack>
                            <button
                              type="button"
                              className="fr-btn fr-btn--tertiary fr-btn--sm"
                              onClick={() => setIsBrowserDemarche(!isBrowserDemarche)}
                            >
                              {browserDemarche}
                            </button>
                          </Stack>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Stack>
              {bo ? (
                <Block>
                  <Box>{hasMobileDemarche}</Box>
                </Block>
              ) : null}
            </Stack>
          </Block>
        </Stack>
      </TemplateBanner>
      <Wrapper>
        {alertInfo && <Box sx={{ p: { xs: 2 } }}>{alertInfo}</Box>}
        <Typography component="h3" sx={{ mt: 3, mb: 5, fontWeight: 700, fontSize: "1.75rem", p: { xs: 2, lg: 0 } }}>
          {t[language].process.steps_title}
        </Typography>
        <Grid container sx={{ justifyContent: "space-between", gap: "2.5rem" }}>
          {bo || displaySummary ? (
            <Grid size={{ xs: 12, lg: 3 }}>
              <Box sx={{ display: { lg: "none" }, "& .MuiFormControlLabel-root": { ml: 1 } }}>
                {bo ? displaySummary : null}
                <Box
                  component="section"
                  className="fr-accordion"
                  sx={{
                    ...(bo ? { opacity: displaySummary?.props?.content?.value === "true" ? 1 : 0.3 } : {}),
                    "&.fr-accordion .fr-collapse--expanded": { py: 0 },
                  }}
                >
                  <Typography component="h3" className="fr-accordion__title">
                    <Box
                      component="button"
                      className="fr-accordion__btn"
                      aria-expanded="false"
                      aria-controls="accordion-etapes"
                    >
                      <Icon iconDSFR="menu-2-fill" sx={{ mr: 1 }} />
                      {t[language].summary.accordion_title}
                    </Box>
                  </Typography>
                  <Box className="fr-collapse" id="accordion-etapes" sx={{ flex: 1 }}>
                    <Summary title={t[language].summary.step_title} items={stepList} />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: { xs: "none", lg: "block" },
                  position: "sticky",
                  top: 0,
                }}
              >
                <Box sx={bo ? { opacity: displaySummary?.props?.content?.value === "true" ? 1 : 0.3 } : {}}>
                  <Summary title={t[language].summary.step_title} items={stepList} />
                </Box>
                {bo ? displaySummary : null}
              </Box>
            </Grid>
          ) : null}
          <Grid
            sx={{
              px: { xs: 2, lg: 0 },
              "& > ol": {
                p: 0,
                my: 0,
                counterReset: "section",
                "& > li::marker": {
                  content: "none",
                  fontSize: 0,
                },
                "& > li": {
                  pb: 0,
                },
              },
            }}
            size={{ xs: 12, lg: bo || displaySummary ? 8 : 12 }}
          >
            <ol className="stepperOl">{stepper}</ol>
          </Grid>
        </Grid>
        {dynamicPart}
      </Wrapper>
    </Box>
  );
};

ProcessDsfr.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default ProcessDsfr;
